import type {
  DatasetsMutatorMutationType,
  DatasetsMutatorCreateItem,
  DatasetsMutatorItem,
  DatasetsMutatorUpdateItem,
  DatasetsMutatorDeleteItem,
  DatasetsMutatorItemStatus,
  UseDatasetsMutatorHandleMutateProps,
  DatasetsMutatorItemPayload,
  DatasetsMutatorRemoveItemProps,
} from "./datasets-mutator.type";
import type { DatasetType } from "@/entities/dataset";

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useEffect } from "react";

import { isDatasetsMutatorItemType } from "./datasets-mutator.helper";
import {
  datasetsMutatorAddItemAtom,
  datasetsMutatorIsDirtyAtom,
  datasetsMutatorIsFinishedAtom,
  datasetsMutatorIsSavingAtom,
  datasetsMutatorItemsAtom,
  datasetsMutatorItemsStatusAtom,
  datasetsMutatorRemoveItemAtom,
  datasetsMutatorStartMutateAtom,
} from "./datasets-mutator.state";

/**
 * Manages the mutator items
 *
 * - Adds a mutator item to the on-going collection
 * - Removes a mutator item from the on-going collection
 *
 * Also reset mutations status in background when adding or removing items
 * to make sure mutations status are always synced with on-going items.
 */
export const useDatasetsMutatorSync = () => {
  const addMutatorItemFn = useSetAtom(datasetsMutatorAddItemAtom);
  const removeMutatorItemFn = useSetAtom(datasetsMutatorRemoveItemAtom);
  const resetItemsStatus = useResetAtom(datasetsMutatorItemsStatusAtom);

  /**
   * Adds a mutator item to the on-going collection
   */
  const addMutatorItem = (item: DatasetsMutatorItemPayload) => {
    resetItemsStatus();
    addMutatorItemFn(item);
  };

  /**
   * Removes a mutator item from the on-going collection
   */
  const removeMutatorItem = (props: DatasetsMutatorRemoveItemProps) => {
    resetItemsStatus();
    removeMutatorItemFn(props);
  };

  return {
    addMutatorItem,
    removeMutatorItem,
  };
};

/**
 * Returns mutations status
 *
 * - `isDirty` = the collection contains items not yet saved
 * - `isSaving` = at least one current dataset mutation is pending
 * - `isFinished` = all the current collection mutations are finished (success or error)
 */
export const useDatasetsMutatorStatus = () => {
  const isDirty = useAtomValue(datasetsMutatorIsDirtyAtom);
  const isSaving = useAtomValue(datasetsMutatorIsSavingAtom);
  const isFinished = useAtomValue(datasetsMutatorIsFinishedAtom);

  return {
    isDirty,
    isSaving,
    isFinished,
  };
};

/**
 * Returns mutations status for a specific dataset type
 *
 * - `isSaving` = a mutation is pending for this dataset type
 * - `toBeDeleted` = a delete mutation for this dataset type is in the collection (not saved yet)
 */
export const useDatasetsMutatorDatasetTypeStatus = (
  datasetType: DatasetType
) => {
  const mutations = useAtomValue(datasetsMutatorItemsAtom);
  const itemsStatus = useAtomValue(datasetsMutatorItemsStatusAtom);

  const datasetTypeDeleteMutations = Object.entries(mutations)
    .filter(
      ([key, mutatorItem]) =>
        key.startsWith(datasetType) && mutatorItem.mutationType === "delete"
    )
    .map(([, mutatorItem]) => mutatorItem);

  const datasetTypeStatus = Object.entries(itemsStatus)
    .filter(([key]) => key.startsWith(datasetType))
    .map(([_, status]) => status);

  const isSaving = datasetTypeStatus.includes("pending");
  const toBeDeleted = datasetTypeDeleteMutations.length > 0;

  return {
    isSaving,
    toBeDeleted,
  };
};

/**
 * Manages the datsets mutator actions
 *
 * - Saves all the mutations stored in the on-going collection
 * - Removes all the mutations stored in the on-going collection
 */
export const useDatasetsMutatorActions = () => {
  const resetItems = useResetAtom(datasetsMutatorItemsAtom);
  const resetItemsStatus = useResetAtom(datasetsMutatorItemsStatusAtom);
  const setStartMutate = useSetAtom(datasetsMutatorStartMutateAtom);

  /**
   * Saves all the mutations stored in the on-going collection
   */
  const saveMutations = () => {
    resetItemsStatus();
    setStartMutate(true);
  };

  /**
   * Removes all the mutations stored in the on-going collection
   */
  const cancelMutations = () => {
    resetItems();
    resetItemsStatus();
  };

  return {
    saveMutations,
    cancelMutations,
  };
};

/**
 * Internal hook to centralize the logic related to executing all the mutations
 *
 * - It listens to `startMutate` to execute the mutation in the collection
 * - It synchornizes the mutation status with the status collection (state)
 * - It resets `startMutate` just after
 */
export const useDatasetsMutatorHandleMutate = ({
  mutatorItems,
  mutationStatus,
  onMutateReady,
}: UseDatasetsMutatorHandleMutateProps) => {
  const [startMutate, setStartMutate] = useAtom(datasetsMutatorStartMutateAtom);
  const removeMutatorItem = useSetAtom(datasetsMutatorRemoveItemAtom);
  const setItemsStatus = useSetAtom(datasetsMutatorItemsStatusAtom);

  const setMutationStatus = (
    { mutationKey }: DatasetsMutatorItem,
    status: DatasetsMutatorItemStatus
  ) => {
    // If mutation is finished, remove related item from accumulator
    const hasFinished = status === "success" || status === "error";
    if (hasFinished) removeMutatorItem({ mutationKey });

    // Then, save mutation states in another atom
    // Just backup "pending", "success" and "error"
    if (status !== "idle") {
      setItemsStatus((state) => ({ ...state, [mutationKey]: status }));
    }

    // Reset `startMutate` for next save button click
    setStartMutate(false);
  };

  useEffect(() => {
    if (startMutate && mutationStatus === "idle") onMutateReady();
  }, [startMutate]);

  useEffect(() => {
    mutatorItems.forEach((mutatorItem) =>
      setMutationStatus(mutatorItem, mutationStatus)
    );
  }, [mutationStatus]);
};

/**
 * Returns mutator items of a specific mutation type from the collection
 */
export const useDatasetMutatorGetItems = <Item extends DatasetsMutatorItem>(
  mutationType: DatasetsMutatorMutationType
) => {
  const accumulator = useAtomValue(datasetsMutatorItemsAtom);

  const items = Object.values(accumulator).filter((item) =>
    isDatasetsMutatorItemType<Item>(item, mutationType)
  );

  return {
    items,
    hasItems: items.length > 0,
  };
};

/**
 * Returns mutator create items from the collection
 */
export const useDatasetMutatorGetCreateItems = () => {
  return useDatasetMutatorGetItems<DatasetsMutatorCreateItem>("create");
};

/**
 * Returns mutator update items from the collection
 */
export const useDatasetMutatorGetUpdateItems = () => {
  return useDatasetMutatorGetItems<DatasetsMutatorUpdateItem>("update");
};

/**
 * Returns mutator delete items from the collection
 */
export const useDatasetMutatorGetDeleteItems = () => {
  return useDatasetMutatorGetItems<DatasetsMutatorDeleteItem>("delete");
};
